import { useEffect, useRef, useState } from "react";
import { Ta } from "#scripts/components/ui/icons.tsx";
import { cn } from "#scripts/lib/utils.ts";

export interface FileViewerFile {
  path: string;
  content: string;
  highlightLine?: number;
}

interface FileViewerProps {
  file: FileViewerFile | null;
}

export function FileViewer({ file }: FileViewerProps) {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const highlightedLineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (file) {
      setIsTransitioning(true);
      const timer = setTimeout(() => setIsTransitioning(false), 400);
      return () => clearTimeout(timer);
    }
  }, [file]);

  useEffect(() => {
    if (file?.highlightLine && highlightedLineRef.current) {
      highlightedLineRef.current.scrollIntoView({ behavior: "instant", block: "center" });
    }
  }, [file]);

  if (!file) {
    return (
      <div data-loc="phosphor-blog/scripts/components/demo/file-viewer.tsx:35:12" data-name="div" className="flex items-center justify-center h-[400px] bg-bg-elevated border border-border rounded-md">
        <div data-loc="phosphor-blog/scripts/components/demo/file-viewer.tsx:36:14" data-name="div" className="text-center text-text-secondary">
          <Ta.IconFileCode data-loc="phosphor-blog/scripts/components/demo/file-viewer.tsx:37:28" data-name="Ta.IconFileCode" className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>Alt + Click on a component to view its source</p>
        </div>
      </div>
    );
  }

  const lines = file.content.split("\n");
  const fileName = file.path.split("/").pop() || file.path;
  const fileDir = file.path.slice(0, file.path.lastIndexOf("/"));

  return (
    <div
      data-loc="phosphor-blog/scripts/components/demo/file-viewer.tsx:50:7" data-name="div" className={cn(
        "bg-bg-elevated border border-border rounded-md overflow-hidden transition-all duration-300",
        isTransitioning && "opacity-50 scale-[0.98]",
      )}
    >
      <div data-loc="phosphor-blog/scripts/components/demo/file-viewer.tsx:55:12" data-name="div" className="flex items-center gap-2 px-4 py-2 bg-bg-elevated border-b border-border">
        <div data-loc="phosphor-blog/scripts/components/demo/file-viewer.tsx:56:14" data-name="div" className="flex gap-1.5">
          <div data-loc="phosphor-blog/scripts/components/demo/file-viewer.tsx:57:16" data-name="div" className="w-3 h-3 rounded-full bg-red-500" />
          <div data-loc="phosphor-blog/scripts/components/demo/file-viewer.tsx:58:16" data-name="div" className="w-3 h-3 rounded-full bg-yellow-500" />
          <div data-loc="phosphor-blog/scripts/components/demo/file-viewer.tsx:59:16" data-name="div" className="w-3 h-3 rounded-full bg-green-500" />
        </div>
        <div data-loc="phosphor-blog/scripts/components/demo/file-viewer.tsx:61:14" data-name="div" className="flex items-center gap-2 text-xs">
          <Ta.IconFileCode data-loc="phosphor-blog/scripts/components/demo/file-viewer.tsx:62:28" data-name="Ta.IconFileCode" className="w-3.5 h-3.5 text-text-tertiary" />
          <span data-loc="phosphor-blog/scripts/components/demo/file-viewer.tsx:63:17" data-name="span" className="text-text-tertiary">{fileDir}/</span>
          <span data-loc="phosphor-blog/scripts/components/demo/file-viewer.tsx:64:17" data-name="span" className="text-text font-medium">{fileName}</span>
        </div>
      </div>
      <div data-loc="phosphor-blog/scripts/components/demo/file-viewer.tsx:67:12" data-name="div" className="overflow-auto max-h-[400px] p-4 font-mono text-xs">
        {lines.map((line, idx) => {
          const lineNumber = idx + 1;
          const isHighlighted = file.highlightLine === lineNumber;

          return (
            <div
              key={idx}
              ref={isHighlighted ? highlightedLineRef : null}
              data-loc="phosphor-blog/scripts/components/demo/file-viewer.tsx:76:15" data-name="div" className={cn("flex gap-3 transition-colors duration-200 whitespace-pre-line", isHighlighted && "bg-accent/10")}
            >
              <span
                data-loc="phosphor-blog/scripts/components/demo/file-viewer.tsx:79:17" data-name="span" className={cn(
                  "select-none text-text-tertiary text-right min-w-[2.5rem]",
                  isHighlighted && "text-accent font-bold",
                )}
              >
                {lineNumber}
              </span>
              <span data-loc="phosphor-blog/scripts/components/demo/file-viewer.tsx:86:21" data-name="span" className={cn("text-text-secondary whitespace-pre", isHighlighted && "text-text")}>{line || " "}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
